import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, ArrowLeft } from "lucide-react";

const DAY_NAME_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function FrequencyBubbles({ value, onChange }) {
  const t = useT();
  const toggle = (i) => {
    const set = new Set(value);
    if (set.has(i)) set.delete(i); else set.add(i);
    onChange(Array.from(set).sort());
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="frequency-bubbles">
      {DAY_NAME_KEYS.map((d, i) => {
        const active = value.includes(i);
        return (
          <button key={d} type="button" onClick={() => toggle(i)}
                  className={`freq-bubble ${active ? "active" : ""}`}
                  data-testid={`freq-day-${i}`}>{t(d)}</button>
        );
      })}
    </div>
  );
}

const sessionLengths = Array.from({ length: 9 }, (_, i) => 10 + i * 10);  // 10..90

export function StudentsListPage() {
  const t = useT();
  const [students, setStudents] = useState([]);
  useEffect(() => { apiClient.get("/students").then((r) => setStudents(r.data.students)); }, []);
  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold">{t("Students")}</h1>
          <p style={{ color: "var(--app-text-muted)" }}>{t("Manage your studio roster.")}</p>
        </div>
        <Link to="/teacher/students/new" className="btn-pill btn-primary" data-testid="new-student-btn"><Plus className="w-4 h-4" /> {t("Add Student")}</Link>
      </div>
      {students.length === 0 ? (
        <div className="bento-card p-10 text-center" data-testid="students-empty">
          <div className="text-5xl mb-3">🎼</div>
          <p>{t("No students yet. Add your first one!")}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.map((s) => (
            <Link key={s.id} to={`/teacher/students/${s.id}`} className="bento-card p-5" data-testid={`student-row-${s.id}`}>
              <div className="font-display text-xl font-bold">{s.name}</div>
              <div className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>
                {s.email || s.phone || "—"}
              </div>
              <div className="flex flex-wrap gap-1 mt-3">
                {(s.frequency_days || []).map((d) => (
                  <span key={d} className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                        style={{ background: "#ffb703", color: "#0a0a0a" }}>{t(DAY_NAME_KEYS[d])}</span>
                ))}
              </div>
              <div className="mt-3 text-xs" style={{ color: "var(--app-text-muted)" }}>
                {s.session_minutes}{t("-min at")} {s.lesson_time}
                {s.will_attend_recital && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px]" style={{ background: "var(--app-accent)", color: "#000" }}>{t("RECITAL")}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const empty = {
  name: "", phone: "", email: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  parent_name: "", parent_phone: "", parent_email: "",
  session_minutes: 30, frequency_days: [], lesson_time: "16:00",
  lessons_end_date: "", daily_practice_goal_minutes: 20,
  will_attend_recital: false, notes: "",
  billing_cadence: "none", billing_amount: "", billing_description: "",
  billing_start_date: "",
};

export function StudentFormPage() {
  const t = useT();
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const nav = useNavigate();
  const [data, setData] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    apiClient.get(`/students/${id}`).then((r) => {
      const s = r.data.student;
      setData({ ...empty, ...s, lessons_end_date: s.lessons_end_date || "" });
    });
  }, [id, isEdit]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...data };
      payload.session_minutes = Number(payload.session_minutes) || 30;
      payload.daily_practice_goal_minutes = Number(payload.daily_practice_goal_minutes) || 20;
      if (!payload.lessons_end_date) payload.lessons_end_date = null;
      if (!payload.email) delete payload.email;
      if (!payload.parent_email) delete payload.parent_email;
      // Billing — coerce amount, null out fields if no cadence
      if (payload.billing_cadence === "none") {
        payload.billing_amount = null;
        payload.billing_description = null;
        payload.billing_start_date = null;
      } else {
        payload.billing_amount = payload.billing_amount === "" ? null : Number(payload.billing_amount);
        if (!payload.billing_start_date) payload.billing_start_date = new Date().toISOString().slice(0, 10);
      }
      if (isEdit) await apiClient.put(`/students/${id}`, payload);
      else await apiClient.post(`/students`, payload);
      toast.success(isEdit ? t("Student updated") : t("Student added"));
      nav("/teacher/students");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Save failed"));
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(t("Delete this student?"))) return;
    await apiClient.delete(`/students/${id}`);
    toast.success(t("Deleted")); nav("/teacher/students");
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="student-form">
      <div className="flex items-center gap-3">
        <Link to="/teacher/students" className="btn-pill" style={{ background: "var(--app-surface-2)", color: "var(--app-text)" }}>
          <ArrowLeft className="w-4 h-4" /> {t("Back")}
        </Link>
        <h1 className="font-display text-3xl font-bold">{isEdit ? t("Edit student") : t("New student")}</h1>
      </div>

      <div className="bento-card p-6 space-y-4">
        <h2 className="font-display font-bold">{t("Contact info")}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>{t("Name *")}</Label><Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} required data-testid="s-name" /></div>
          <div><Label>{t("Student email")}</Label><Input type="email" value={data.email} onChange={(e) => setData({...data, email: e.target.value})} data-testid="s-email" /></div>
          <div><Label>{t("Student phone")}</Label><Input value={data.phone} onChange={(e) => setData({...data, phone: e.target.value})} data-testid="s-phone" /></div>
          <div><Label>{t("Emergency contact name")}</Label><Input value={data.emergency_contact_name} onChange={(e) => setData({...data, emergency_contact_name: e.target.value})} /></div>
          <div><Label>{t("Emergency phone")}</Label><Input value={data.emergency_contact_phone} onChange={(e) => setData({...data, emergency_contact_phone: e.target.value})} /></div>
          <div><Label>{t("Parent name")}</Label><Input value={data.parent_name} onChange={(e) => setData({...data, parent_name: e.target.value})} /></div>
          <div><Label>{t("Parent phone")}</Label><Input value={data.parent_phone} onChange={(e) => setData({...data, parent_phone: e.target.value})} /></div>
          <div><Label>{t("Parent email")}</Label><Input type="email" value={data.parent_email} onChange={(e) => setData({...data, parent_email: e.target.value})} /></div>
        </div>
      </div>

      <div className="bento-card p-6 space-y-5">
        <h2 className="font-display font-bold">{t("Lesson settings")}</h2>
        <div>
          <Label>{t("Length of session")}</Label>
          <Select value={String(data.session_minutes)} onValueChange={(v) => setData({ ...data, session_minutes: Number(v) })}>
            <SelectTrigger className="max-w-xs" data-testid="session-length"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sessionLengths.map((m) => (<SelectItem key={m} value={String(m)}>{m} {t("minutes")}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("Frequency setting")}</Label>
          <p className="text-xs mb-2" style={{ color: "var(--app-text-muted)" }}>{t("Tap bubbles to select lesson days.")}</p>
          <FrequencyBubbles value={data.frequency_days} onChange={(v) => setData({ ...data, frequency_days: v })} />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div><Label>{t("Time of day")}</Label><Input type="time" value={data.lesson_time} onChange={(e) => setData({...data, lesson_time: e.target.value})} data-testid="lesson-time" /></div>
          <div><Label>{t("Lessons end date (optional)")}</Label><Input type="date" value={data.lessons_end_date || ""} onChange={(e) => setData({...data, lessons_end_date: e.target.value})} data-testid="lessons-end" /></div>
          <div><Label>{t("Daily practice goal (minutes)")}</Label><Input type="number" min={5} value={data.daily_practice_goal_minutes} onChange={(e) => setData({...data, daily_practice_goal_minutes: Number(e.target.value)})} /></div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={data.will_attend_recital} onCheckedChange={(v) => setData({ ...data, will_attend_recital: v })} data-testid="recital-toggle" />
          <span>{t("Student will be in recital")}</span>
        </div>
        <div>
          <Label>{t("Notes")}</Label>
          <Textarea value={data.notes || ""} onChange={(e) => setData({...data, notes: e.target.value})} rows={3} />
        </div>
      </div>

      <div className="bento-card p-6 space-y-5" data-testid="billing-card">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <h2 className="font-display font-bold">{t("Recurring billing")}</h2>
          <div className="text-xs" style={{ color: "var(--app-text-muted)" }}>
            {t("Invoices auto-generate on the due date.")}
          </div>
        </div>
        <div>
          <Label>{t("Billing cadence")}</Label>
          <Select value={data.billing_cadence || "none"} onValueChange={(v) => setData({ ...data, billing_cadence: v })}>
            <SelectTrigger className="max-w-xs" data-testid="billing-cadence"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("No recurring billing")}</SelectItem>
              <SelectItem value="weekly">{t("Weekly")}</SelectItem>
              <SelectItem value="biweekly">{t("Every 2 weeks")}</SelectItem>
              <SelectItem value="monthly">{t("Monthly")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {data.billing_cadence !== "none" && data.billing_cadence && (
          <div className="grid md:grid-cols-3 gap-4" data-testid="billing-fields">
            <div>
              <Label>{t("Amount per invoice (USD)")}</Label>
              <Input
                type="number" step="0.01" min={0}
                value={data.billing_amount ?? ""}
                onChange={(e) => setData({ ...data, billing_amount: e.target.value })}
                data-testid="billing-amount"
                placeholder={t("e.g. 120")}
              />
            </div>
            <div>
              <Label>{t("Description on invoice")}</Label>
              <Input
                value={data.billing_description || ""}
                onChange={(e) => setData({ ...data, billing_description: e.target.value })}
                placeholder={t("e.g. Weekly piano lessons")}
                data-testid="billing-description"
              />
            </div>
            <div>
              <Label>{t("Start date (first invoice)")}</Label>
              <Input
                type="date"
                value={data.billing_start_date || ""}
                onChange={(e) => setData({ ...data, billing_start_date: e.target.value })}
                data-testid="billing-start-date"
              />
              <p className="text-xs mt-1" style={{ color: "var(--app-text-muted)" }}>
                {t("Leave blank to start today. Past dates will backfill missed invoices.")}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          {isEdit && (
            <Button type="button" variant="destructive" onClick={del} data-testid="delete-student">
              <Trash2 className="w-4 h-4 mr-2" /> {t("Delete")}
            </Button>
          )}
        </div>
        <Button type="submit" disabled={busy} className="btn-pill btn-primary h-11" data-testid="save-student">
          {busy ? t("Saving...") : (isEdit ? t("Save changes") : t("Add student"))}
        </Button>
      </div>
    </form>
  );
}

export function StudentDetailPage() {
  const t = useT();
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [streaks, setStreaks] = useState(null);
  const [songs, setSongs] = useState([]);
  const [curriculum, setCurriculum] = useState(null);
  useEffect(() => {
    apiClient.get(`/students/${id}`).then((r) => setStudent(r.data.student));
    apiClient.get(`/practice/streaks/${id}`).then((r) => setStreaks(r.data));
    apiClient.get(`/songs?student_id=${id}`).then((r) => setSongs(r.data.songs));
    apiClient.get(`/students/${id}/curriculum-progress`).then((r) => setCurriculum(r.data)).catch(() => {});
  }, [id]);

  if (!student) return <div data-testid="loading">{t("Loading…")}</div>;
  return (
    <div className="space-y-6" data-testid="student-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/teacher/students" className="text-sm font-semibold" style={{ color: "var(--app-primary)" }}>{t("← Back to students")}</Link>
          <h1 className="font-display text-4xl font-bold mt-1">{student.name}</h1>
        </div>
        <Link to={`/teacher/students/${id}/edit`} className="btn-pill btn-primary" data-testid="edit-student">{t("Edit")}</Link>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bento-card p-5"><div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Daily Streak")}</div><div className="font-display text-4xl font-bold">{streaks?.daily_streak ?? 0}</div></div>
        <div className="bento-card p-5"><div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Full Session Streak")}</div><div className="font-display text-4xl font-bold">{streaks?.full_session_streak ?? 0}</div></div>
        <div className="bento-card p-5"><div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Total Sessions")}</div><div className="font-display text-4xl font-bold">{streaks?.total_sessions ?? 0}</div></div>
        <div className="bento-card p-5"><div className="text-xs uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Total Minutes")}</div><div className="font-display text-4xl font-bold">{streaks?.total_minutes ?? 0}</div></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bento-card p-6">
          <h2 className="font-display font-bold text-xl mb-3">{t("Lesson settings")}</h2>
          <ul className="space-y-1 text-sm">
            <li><b>{t("Session:")}</b> {student.session_minutes} {t("min")} {t("at")} {student.lesson_time}</li>
            <li><b>{t("Days:")}</b> {(student.frequency_days || []).map((i) => t(DAY_NAME_KEYS[i])).join(", ") || "—"}</li>
            <li><b>{t("End date:")}</b> {student.lessons_end_date || "—"}</li>
            <li><b>{t("Daily goal:")}</b> {student.daily_practice_goal_minutes} {t("min")}</li>
            <li><b>{t("Recital:")}</b> {student.will_attend_recital ? t("Yes") : t("No")}</li>
            <li data-testid="detail-billing">
              <b>{t("Billing:")}</b>{" "}
              {student.billing_cadence && student.billing_cadence !== "none"
                ? <>${Number(student.billing_amount || 0).toFixed(2)} {student.billing_cadence}{student.billing_next_due ? ` ${t("— next")} ${student.billing_next_due}` : ""}</>
                : <span style={{ color: "var(--app-text-muted)" }}>{t("No recurring billing set")}</span>}
            </li>
          </ul>
        </div>
        <div className="bento-card p-6">
          <h2 className="font-display font-bold text-xl mb-3">{t("Contact")}</h2>
          <ul className="space-y-1 text-sm">
            <li><b>{t("Email:")}</b> {student.email || "—"}</li>
            <li><b>{t("Phone:")}</b> {student.phone || "—"}</li>
            <li><b>{t("Parent:")}</b> {student.parent_name || "—"} {student.parent_phone && `(${student.parent_phone})`}</li>
            <li><b>{t("Emergency:")}</b> {student.emergency_contact_name || "—"} {student.emergency_contact_phone && `(${student.emergency_contact_phone})`}</li>
          </ul>
        </div>
      </div>
      <div className="bento-card p-6">
        <h2 className="font-display font-bold text-xl mb-3">{t("Songs (this student)")}</h2>
        {songs.length === 0 ? <p style={{ color: "var(--app-text-muted)" }}>{t("No songs yet.")}</p> : (
          <ul className="space-y-2">
            {songs.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "var(--app-border)" }}>
                <div>🎵 <b>{s.title}</b> {s.composer && <span className="text-sm" style={{ color: "var(--app-text-muted)" }}>— {s.composer}</span>}</div>
                <div className="text-xs">{s.status.replace("_", " ")}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {curriculum && curriculum.weeks && curriculum.weeks.length > 0 && (
        <div className="bento-card p-6" data-testid="teacher-view-curriculum-progress">
          <h2 className="font-display font-bold text-xl mb-3">{t("Curriculum progress")}</h2>
          <ul className="space-y-2">
            {curriculum.weeks.map((w) => {
              const fp = w.focus_points || [];
              const done = fp.filter((_, i) => curriculum.progress?.[w.id]?.[String(i)]?.completed).length;
              const weekDone = curriculum.progress?.[w.id]?.["-1"]?.completed;
              const pct = fp.length > 0 ? Math.round((done / fp.length) * 100) : (weekDone ? 100 : 0);
              return (
                <li key={w.id} className="py-2 border-b" style={{ borderColor: "var(--app-border)" }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold"
                            style={{ background: weekDone ? "var(--app-accent)" : "var(--app-surface-2)",
                                     color: weekDone ? "#0a0a0a" : "var(--app-text-muted)" }}>
                        {w.week_number}
                      </span>
                      <b>{w.title}</b>
                    </div>
                    <span className="text-xs" style={{ color: weekDone ? "var(--app-accent)" : "var(--app-text-muted)" }}>
                      {weekDone ? t("Week complete ✓") : (fp.length > 0 ? `${done}/${fp.length} ${t("goals")}` : "—")}
                    </span>
                  </div>
                  {fp.length > 0 && (
                    <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--app-surface-2)" }}>
                      <div className="h-full" style={{ width: `${pct}%`, background: "var(--app-accent)", transition: "width 200ms ease" }} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
